import {GroupedRowState} from "../classes/GroupedRowState";
import {makeAutoObservable, reaction} from "mobx";
import {CategorizationReviewPageController} from "./CategorizationReviewPageController";
import {PartRowState} from "../classes/PartRowState";
import MithraMaterializedApi from "../../../services/MithraMaterializedApi";
import {from, Subscription} from "rxjs";
import {AggregatedStatusResults} from "../../../services/ApiTypes";
import {MatSGroupAggregate} from "../../../services/classes/MaterializedClasses";
import {URLSearchParams} from "node:url";
import {MatSupplierFilter} from "../../../services/classes/MatReviewClasses";
import {getAcceptAlertMessage, getEditAlertMessage} from "../components/review-table/selection/SelectionMessages";

export class CategorizationReviewPageSelectionController {

    public readonly totalCountController = new TotalCountController(this.materializedApi);

    /**
     * If everything is selected
     * including the items not visible in the current page
     */
    isAllSelected = false;

    lastSelected: PartRowState | undefined = undefined;

    /**
     * The page size
     */
    pageSize = 0;
    /**
     * Total elements in the backend
     */
    nTotal: number | null = null;

    constructor(
        private categorizationReviewPageController: CategorizationReviewPageController,
        private materializedApi: MithraMaterializedApi,
    ) {
        makeAutoObservable(this);

        reaction(() => ({
            singleMode: this.categorizationReviewPageController.singleMode,
            partPageSize: this.dataController.partPages.pageSize,
            partCount: this.dataController.partPages.count,
            supplierPageSize: this.dataController.supplierPages.pageSize,
            supplierCount: this.dataController.supplierPages.count,

            // Ensure we also trigger selection reset when the data changes
            _partData: this.dataController.partPages.data,
            _supplierData: this.dataController.supplierPages.data,
        }), ({singleMode, partPageSize, partCount, supplierPageSize, supplierCount}) => {
            // The data or the view has changed
            if (singleMode) {
                this.pageSize = partPageSize;
                this.nTotal = partCount;
            } else {
                this.pageSize = supplierPageSize;
                this.nTotal = supplierCount;
            }
            this.isAllSelected = false;
        })

        reaction(
            () => [
                this.categorizationReviewPageController.singleMode,
                this.categorizationReviewPageController.reviewPageFilterController.partFilter,
                this.selectionState === 'all',
            ] as const,
            ([singleMode, filter, isAll]) => {
                if (!isAll) return;
                if (singleMode) {
                    this.totalCountController._loadParts(filter);
                }
            },
        )
        reaction(
            () => [
                this.categorizationReviewPageController.singleMode,
                this.categorizationReviewPageController.reviewPageFilterController.selectedFilter,
                this.selectionState === 'all',
            ] as const,
            ([singleMode, filter, isAll]) => {
                if (!isAll) return;
                if (!singleMode) {
                    this.totalCountController._loadGroups(filter);
                }
            },
        )
    }

    get dataController() {
        return this.categorizationReviewPageController.reviewPageDataController;
    }

    get hasSelectableElementsOutsideView(): boolean {
        if (this.nTotal === null) return false;
        return this.nTotal > this.pageSize;
    }

    get isAllInViewSelected(): boolean {
        if (this.nSelectablesInView === 0) return false;
        // TODO CAT-2560: How about disabled rows?
        return this.nInViewSelected() === this.nSelectablesInView;
    }

    /**
     * The total number of (partially) selected rows
     */
    get nPartialSelected(): number {
        if (this.isAllSelected) {
            return this.nTotal || 0;
        }
        return this.nInViewSelected();
    }

    /**
     * All the elements are selected
     * or partially selected (in the grouped mode)
     *
     * For reactivity this needs to be a mobx-action, not sure why...
     */
    nInViewSelected(): number {
        const singleMode = this.categorizationReviewPageController.singleMode;
        if (singleMode) {
            return this.selectedPartsInView.length;
        } else {
            return this.selectedSuppliersInView.length;
        }
    }

    get isAnySelected() {
        return this.isAllSelected || this.nInViewSelected() > 0;
    }

    get selectionState(): 'all' | 'some' | 'none' {
        if (this.isAllSelected) {
            return 'all';
        }
        if (this.nSelected === 0) {
            return 'none';
        }
        return 'some';
    }

    /**
     * The effective selection size
     */
    get nSelected(): number {
        if (this.isAllSelected) {
            if (this.nTotal === null) {
                console.warn('nTotal is not expected to be null')
                return 0;
            }
            return this.nTotal;
        }
        return this.nInViewSelected();
    }

    /**
     * Get the total number of groups that are completely selected
     */
    get nFullySelectedSuppliers(): number {
        const singleMode = this.categorizationReviewPageController.singleMode;
        if (this.isAllSelected || singleMode) {
            return this.nSelected;
        }
        return this.fullySelectedSuppliersInView.length;
    }

    /**
     * The number of parts selected individually in the grouped mode
     */
    get nSelectedParts(): number {
        const singleMode = this.categorizationReviewPageController.singleMode;
        if (singleMode) {
            return 0;
        }
        // noinspection UnnecessaryLocalVariableJS
        const sum = this.dataController.supplierPages.data?.reduce((acc, r) => {
            if (r.isAllSelected) {
                // Skip
            } else {
                acc += r.nInViewSelected;
            }
            return acc;
        }, 0) ?? 0;
        return sum;
    }

    /**
     * When the user changes the selection of a supplier group
     * @param groupedRow
     * @param selected
     */
    onSetSelectionOfSupplierGroup(groupedRow: GroupedRowState, selected: boolean) {
        this._setGroupSelected(groupedRow, selected)
        this.reCalcAllSelectedRoot(selected);
    }

    reCalcAllSelectedRoot(newSelection: boolean) {
        if (!newSelection) {
            // A part is deselected
            this.isAllSelected = false;
        } else {
            // A part is selected
            if (this.hasSelectableElementsOutsideView) {
                // We do not allow the user to select parts outside the view indirectly
                // This needs to be done by selecting everything explicitly
                this.isAllSelected = false;
            } else {
                this.isAllSelected = this.isAllInViewSelected;
            }
        }
    }

    /**
     * When the user changes the selection of the entire table
     * @param selected
     */
    public onSetAllSelected(selected: boolean) {
        this.isAllSelected = selected;
        const singleMode = this.categorizationReviewPageController.singleMode;
        if (singleMode) {
            this.dataController.partPages.data?.forEach(p => CategorizationReviewPageSelectionController._setSelected(p, selected));
        } else {
            this.dataController.supplierPages.data?.forEach(g => this._setGroupSelected(g, selected))
        }
    }

    onDeselectAll() {
        this.onSetAllSelected(false);
    }

    onClickToggleAllSelected() {
        switch (this.selectionState) {
            case 'none':
                this.onSetAllSelected(true)
                break;
            case 'some':
                this.onSetAllSelected(true)
                break
            case 'all':
                // Everything is already selected so simply reset the filter
                this.onSetAllSelected(false);
                break
        }
    }

    /**
     * Set or reset a selection by not interacting with the selection component
     * @param subRow
     * @param selected
     */
    setSelectionSubRow(subRow: PartRowState, selected: boolean) {
        CategorizationReviewPageSelectionController._setSelected(subRow, selected);
        if (subRow.parentRow) {
            subRow.parentRow.reCalcAllSelected(selected);
        }
        this.reCalcAllSelectedRoot(selected);
    }

    /**
     * The user interacts with the selection through the selection component
     *
     * It remembers the last selection and has the option to select all between the last selection and this selection
     * @param subRow
     * @param allBetween
     */
    onClickToggleSelectionSubRow(subRow: PartRowState, allBetween: boolean = false) {
        if (!allBetween) {
            this.setSelectionSubRow(subRow, !subRow.selected);
            return;
        }

        // Collect statistics on how much this is used
        console.warn('onClickToggleSelectionSubRow clicked with allBetween ', subRow.id)
        // This functionality is of the spec, as this is too complex and we do not really use it

        // Unselect all parents
        this.resetSupplierSelection();

        console.log('toggleSelectionSubRow', subRow.id);
        const prevLastSelected = this.lastSelected;

        // Check if the last selection makes sense or not
        if (!prevLastSelected) {
            console.warn('No previous selection found');
            return;
        }
        // Check if we can find the index
        const data = this.dataController.allPartRows;
        if (!data || data.length === 0) {
            console.warn('No data found');
            return;
        }
        const prevIndex = data.findIndex(p => p === prevLastSelected);
        if (prevIndex === -1) {
            console.warn('Previous selection not found in the current page');
            return;
        }
        // Check if we can find the index of this selection
        const thisIndex = data.findIndex(p => p === subRow);
        if (thisIndex === -1) {
            console.warn('This selection not found in the current page');
            return;
        }

        const parts = data.slice(Math.min(prevIndex, thisIndex), Math.max(prevIndex, thisIndex) + 1);
        this.toggleMultipleParts(parts);

        // if (prevLastSelected) {
        //     prevLastSelected.isLastSelected = false;
        // }
        // subRow.isLastSelected = true;
        this.lastSelected = subRow;
    }

    toggleMultipleParts(subRows: PartRowState[]) {
        const selected = !subRows.every(p => !p.combinedStateVisual.selectionDisabled && p.selected);
        subRows.forEach(p => CategorizationReviewPageSelectionController._setSelected(p, selected))
        this.reCalcAllSelectedRoot(selected);
        return selected;
    }

    get selectedPartsInView() {
        const singleMode = this.categorizationReviewPageController.singleMode;
        console.assert(singleMode, 'This is only implemented for single mode')
        return this.dataController.partPages.data?.filter(s => s.selected) || [];
    }

    /**
     * Get the selected suppliers
     * Also includes the partially selected suppliers
     */
    get selectedSuppliersInView(): GroupedRowState[] {
        const singleMode = this.categorizationReviewPageController.singleMode;
        console.assert(!singleMode, 'This is only implemented for grouped mode')
        return this.dataController.supplierPages.data?.filter(s => s.hasSelection) || [];
    }

    /**
     * Get the selected suppliers
     * Only include suppliers that are completely selected
     */
    get fullySelectedSuppliersInView(): GroupedRowState[] {
        const singleMode = this.categorizationReviewPageController.singleMode;
        console.assert(!singleMode, 'This is only implemented for grouped mode')
        return this.dataController.supplierPages.data?.filter(s => s.isAllSelected) || [];
    }

    get nSelectablesInView() {
        const singleMode = this.categorizationReviewPageController.singleMode;
        if (singleMode) {
            return this.dataController.partPages.data?.length || 0;
        } else {
            return this.dataController.supplierPages.data?.length || 0;
        }
    }

    reset() {
        this.isAllSelected = false;
        this.nTotal = null
        this.pageSize = 0
        this.lastSelected = undefined

        // We can potentially speed this up by resetting the data fields, instead of looping over all the rows
        this.dataController.partPages.data?.forEach(p => p.selected = false);
        this.dataController.supplierPages.data?.forEach(r => {
            r.isAllSelected = false;
            r.partStates?.forEach(p => p.selected = false);
        })
    }

    resetSupplierSelection() {
        this.isAllSelected = false;
        this.dataController.supplierPages.data?.forEach(r => {
            r.isAllSelected = false;
            r.partStates?.forEach(p => p.selected = false);
        })
    }

    public static _setSelected(p: PartRowState, selected: boolean) {
        if (!p.combinedStateVisual.selectionDisabled) {
            p.selected = selected;
        } else {
            p.selected = false;
        }
    }

    public _setGroupSelected(g: GroupedRowState, selected: boolean) {
        if (!g.combinedStateVisual.selectionDisabled) {
            g.isAllSelected = selected;
            g.partStates?.forEach(p => CategorizationReviewPageSelectionController._setSelected(p, selected));
        } else {
            g.isAllSelected = false;
        }
    }

    get selectionIsLoading() {
        if (this.selectionState !== 'all') return false;
        if (this.categorizationReviewPageController.singleMode) {
            return this.totalCountController.resultParts === null;
        } else {
            return this.totalCountController.resultGroups === null;
        }
    }

    get editCategoryAlertMessage() {
        const isAll = this.selectionState === 'all';
        let n: number | null;
        let n1: number | null;
        if (this.categorizationReviewPageController.singleMode) {
            const total = this.totalCountController.resultParts;
            if (isAll) {
                if (total) {
                    n = total.n_parts;
                    n1 = total.n_pending;
                } else {
                    n = null
                    n1 = null
                }
            } else {
                n = this.nInViewSelected();
                n1 = 0; // It's not possible to directly select pending parts
            }
        } else {
            const total = this.totalCountController.resultGroups;
            if (isAll) {
                if (total) {
                    n = total.total_n_parts;
                    n1 = total.total_n_pending;
                } else {
                    n = null
                    n1 = null
                }
            } else {
                let nParts = 0;
                let nPending = 0;
                this.selectedSuppliersInView.forEach(g => {
                    if (g.isAllSelected) {
                        nParts += g.data.total_n_parts;
                        nPending += g.data.total_n_pending;
                    } else {
                        g.partStates?.forEach(p => {
                            if (p.selected) {
                                nParts++;
                                // It's not possible to directly select pending parts
                            }
                        })
                    }
                })
                n = nParts;
                n1 = nPending;
            }
        }
        return getEditAlertMessage(n, n1);
    }

    get acceptCategoryAlertMessage() {
        const isAll = this.selectionState === 'all';

        let n: number | null;
        let n1: number | null;
        let n2: number | null;
        if (this.categorizationReviewPageController.singleMode) {
            if (isAll) {
                const total = this.totalCountController.resultParts;
                if (total !== null) {
                    n = total.n_parts;
                    n1 = total.n_pending;
                    n2 = total.n_is_open_uncat;
                } else {
                    n = null;
                    n1 = null;
                    n2 = null;
                }
            } else {
                n = this.nInViewSelected();
                n1 = 0; // It's not possible to directly select pending parts
                n2 = this.nIsOpenAndUncategorized || 0;
            }
        } else {
            if (isAll) {
                const total = this.totalCountController.resultGroups;
                if (total) {
                    n = total.total_n_parts;
                    n1 = total.total_n_pending;
                    n2 = total.total_n_is_open_uncat;
                } else {
                    n = null
                    n1 = null
                    n2 = null
                }
            } else {
                let nParts = 0;
                let nPending = 0;
                let nOpenUncat = 0;
                this.selectedSuppliersInView.forEach(g => {
                    if (g.isAllSelected) {
                        nParts += g.data.total_n_parts;
                        nOpenUncat += g.data.total_n_is_open_uncat;
                        nPending += g.data.total_n_pending;
                    } else {
                        g.partStates?.forEach(p => {
                            if (p.selected) {
                                nParts++;
                                if (p.isOpenAndUncategorized) {
                                    nOpenUncat++;
                                }
                                // It's not possible to directly select pending parts
                            }
                        })
                    }
                })
                n = nParts;
                n1 = nPending;
                n2 = nOpenUncat;
            }
        }
        return getAcceptAlertMessage(n, n1, n2);
    }

    get nIsOpenAndUncategorized(): number | null {
        if (this.categorizationReviewPageController.singleMode) {
            if (this.isAllSelected) {
                if (!this.totalCountController.resultParts) {
                    return null;
                }
                return this.totalCountController.resultParts.n_is_open_uncat;
            } else {
                return this.selectedPartsInView.filter(p => p.isOpenAndUncategorized).length;
            }
        }
        // TODO CAT-2560: Can be refactored
        return null
    }

    get totalSelectedParts(): number | null {
        if (this.categorizationReviewPageController.singleMode) {
            if (this.isAllSelected) {
                if (!this.totalCountController.resultParts) return null;
                return this.totalCountController.resultParts.n_parts - this.totalCountController.resultParts.n_pending;
            } else {
                return this.nInViewSelected();
            }
        } else {
            if (this.isAllSelected) {
                if (!this.totalCountController.resultGroups) return null;
                return this.totalCountController.resultGroups.total_n_parts - this.totalCountController.resultGroups.total_n_pending;
            } else {
                // Each fully selected needs to be counted
                let nParts = 0;
                this.selectedSuppliersInView.forEach(g => {
                    if (g.isAllSelected) {
                        // Get the numbers from the API resp
                        nParts += g.data.total_n_parts - g.data.total_n_pending;
                    } else {
                        // Count the selected parts
                        nParts += g.partStates?.filter(p => p.selected).length || 0;
                    }
                })
                return nParts;
            }
        }
    }

    get totalSelectedGroups(): number | null {
        if (this.categorizationReviewPageController.singleMode) {
            return null;
        }
        if (this.isAllSelected) {
            if (!this.totalCountController.resultGroups) return null;
            return this.totalCountController.resultGroups.total_n_suppliers - this.totalCountController.resultGroups.total_n_suppliers_with_pending;
        } else {
            return this.selectedSuppliersInView.length
        }
    }

    get totalParts(): number | null {
        if (this.nTotal === null) return null;
        return this.nTotal;
    }
}

class TotalCountController {
    _loader?: Subscription;
    resultParts: { databag: number } & AggregatedStatusResults | null = null;
    resultGroups: { databag: number } & MatSGroupAggregate | null = null;

    constructor(private materializedApi: MithraMaterializedApi) {
        makeAutoObservable(this, {_loader: false, _loadParts: false, _loadGroups: false});
    }

    _loadParts(filter: URLSearchParams | undefined) {
        if (!filter) return;
        const databagId = filter.get('databag') ? Number(filter.get('databag')) : -1;
        if (databagId <= 0) return;

        this.reset()
        const abort = new AbortController();
        this._loader = from(this.materializedApi.getReviewPartsTotal(filter, {signal: abort.signal})).subscribe({
            next: (res) => this.setTotalParts({
                ...res.data,
                databag: databagId,
            }),
        })
        this._loader.add(() => abort.abort());
    }

    _loadGroups(filter: MatSupplierFilter | undefined) {
        if (!filter) return;
        if (filter.databag <= 0) return;

        this.reset()
        const abort = new AbortController();
        this._loader = from(this.materializedApi.getCategoryReviewTotals(filter, {signal: abort.signal})).subscribe({
            next: (res) => this.setTotalGroups({
                ...res.data,
                databag: filter.databag,
            }),
        })
        this._loader.add(() => abort.abort());
    }

    reset() {
        if (this._loader) {
            this._loader.unsubscribe();
        }
        this.resultParts = null;
        this.resultGroups = null;
    }

    setTotalParts(result: this['resultParts']) {
        this.resultParts = result;
    }

    setTotalGroups(result: this['resultGroups']) {
        this.resultGroups = result;
    }
}
